// Considered: AddChannelDialogOrganism (marketplace) — rejected. That organism is
// channel-first: it browses/searches all approved channels to pick which to subscribe a
// sphere into (a multi-channel, organism-scale search+filter state). This dialog is
// item-fixed — one context, listed into exactly one already-known-manageable channel — so
// the picker is a single DropdownAtom fed by `useManageableChannels()`, not a search list.
// Reused from AddChannelDialogOrganism: the DialogWrapper shell conventions only (overlay,
// focus, Escape-to-close), not its search/multi-select state.
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import DialogWrapper from '../../molecules/DialogWrapper/DialogWrapper';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useManageableChannels } from '../../../hooks/useManageableChannels';
import { addChannelItem } from '../../../api/marketplaceApi';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ListInChannelDialogOrganism.scss';

export interface ListInChannelDialogOrganismProps {
  visible: boolean;
  onHide: () => void;
  itemType: 'context';
  itemSystemName: string;
  /** Channel ids the item is already listed in — excluded from the picker. */
  alreadyListedChannelIds: string[];
  onListed?: (channelId: string) => void;
}

function getErrorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

const ListInChannelDialogOrganism: React.FC<ListInChannelDialogOrganismProps> = ({
  visible,
  onHide,
  itemType,
  itemSystemName,
  alreadyListedChannelIds,
  onListed,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { channels, loading: channelsLoading, error: channelsError } = useManageableChannels();

  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () =>
      channels
        .filter((channel) => !alreadyListedChannelIds.includes(channel.id))
        .map((channel) => ({
          label: getLocalizedText(channel.name) || channel.system_name,
          value: channel.id,
        })),
    [channels, alreadyListedChannelIds]
  );

  const resetAndHide = () => {
    setSelectedChannelId(null);
    setError(null);
    setSubmitting(false);
    onHide();
  };

  const handleConfirm = async () => {
    if (!apiClients?.schemaClient || !selectedChannelId) return;

    setSubmitting(true);
    setError(null);

    try {
      await addChannelItem(apiClients.schemaClient, selectedChannelId, {
        itemType,
        itemSystemName,
      });
      onListed?.(selectedChannelId);
      resetAndHide();
    } catch (err) {
      const status = getErrorStatus(err);
      // Distinct copy per status — the server distinguishes "you may manage this channel but not
      // distribute this template" (403, assertCanListTemplate's distribute gate) from "the item
      // couldn't be resolved server-side" (422, unknown/unresolvable template type) from a
      // same-channel duplicate (409). Collapsing these into one generic message would hide which
      // of "pick a different channel" vs "this item can't be listed at all" the user needs to do.
      if (status === 409) {
        setError(t('studio.gallery.drawer.distribution.listInChannelDialog.alreadyListedError'));
      } else if (status === 403) {
        setError(t('studio.gallery.drawer.distribution.listInChannelDialog.distributeDeniedError'));
      } else if (status === 422) {
        setError(t('studio.gallery.drawer.distribution.listInChannelDialog.unresolvableError'));
      } else {
        setError(t('studio.gallery.drawer.distribution.listInChannelDialog.genericError'));
      }
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <DialogWrapper
      header={t('studio.gallery.drawer.distribution.listInChannelDialog.title')}
      visible={visible}
      onHide={resetAndHide}
      className="list-in-channel-dialog"
    >
      <div className="list-in-channel-dialog__body">
        <label className="list-in-channel-dialog__label" htmlFor="list-in-channel-dropdown">
          {t('studio.gallery.drawer.distribution.listInChannelDialog.channelLabel')}
        </label>
        <DropdownAtom
          inputId="list-in-channel-dropdown"
          value={selectedChannelId}
          options={options}
          onChange={(e) => setSelectedChannelId(e.value)}
          placeholder={t('studio.gallery.drawer.distribution.listInChannelDialog.channelPlaceholder')}
          disabled={submitting || channelsLoading || channelsError}
        />

        {/* `useManageableChannels` fails CLOSED — a failed `can_manage_content` lookup yields an
            empty list, which is indistinguishable from "you manage no channels" unless the two
            are named apart. Silence here reads as a permissions verdict the server never gave. */}
        {channelsError && (
          <MessageAtom
            severity="warn"
            text={t('studio.gallery.drawer.distribution.listInChannelDialog.channelsUnavailable')}
            className="list-in-channel-dialog__error"
          />
        )}
        {!channelsError && !channelsLoading && options.length === 0 && (
          <MessageAtom
            severity="info"
            text={t('studio.gallery.drawer.distribution.listInChannelDialog.noManageableChannels')}
            className="list-in-channel-dialog__error"
          />
        )}

        {error && (
          <MessageAtom severity="error" text={error} className="list-in-channel-dialog__error" />
        )}

        <div className="list-in-channel-dialog__actions">
          <ButtonAtom
            label={t('studio.gallery.drawer.distribution.listInChannelDialog.cancel')}
            variant="text"
            onClick={resetAndHide}
            disabled={submitting}
          />
          <ButtonAtom
            label={t('studio.gallery.drawer.distribution.listInChannelDialog.confirm')}
            onClick={handleConfirm}
            disabled={!selectedChannelId || submitting}
          />
        </div>
      </div>
    </DialogWrapper>
  );
};

ListInChannelDialogOrganism.displayName = 'ListInChannelDialogOrganism';

export default ListInChannelDialogOrganism;
